import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import NotFound from 'wanderu-component-not-found';
import { fetchAllTripsAction } from 'wanderu-ducks-psearch';
import { cityToCitySelectors as c2cSelectors } from 'wanderu-ducks-seo';
import { splitTestSelectors } from 'wanderu-ducks-split-test';
import { localize, selectLocale, i18nMoment } from 'wanderu-i18n';
import Widget from 'wanderu-widget';

import MultiModalCityToCity from '../../components/MultiModalCityToCity/MultiModalCityToCity';
import { getPSearchParams } from '../../utils';
import modeInfo from './modeData';
import testModeInfo from './modeDataTest';

const INDEXABLE_URLS = [];

function MultiModalCityToCityContainer(props) {
    const {
        data,
        fetchAllTrips,
        history,
        isFetchingAllTrips,
        locale,
        noIndex,
        path,
        psearchParams,
        searchParams,
        staticContext,
        trips,
    } = props;

    useEffect(() => {
        if (data?.showTrips) {
            fetchAllTrips(psearchParams);
        }
    }, [
        data,
        fetchAllTrips,
        psearchParams,
    ]);

    if (staticContext) {
        staticContext.cache = {
            secondsFromNow: 60 * 60 * 24,
        };

        if (staticContext.url) {
            return null;
        } if (!data) {
            staticContext.status = 404;
        }
    }

    if (!data) {
        return <NotFound />;
    }

    return (
        <MultiModalCityToCity
            arrivePlace={data.arrivePlace}
            carousel={data.carousel}
            carriers={data.carriers}
            departPlace={data.departPlace}
            destinationPlaceId={data.destinationPlaceId}
            history={history}
            isFetchingAllTrips={isFetchingAllTrips}
            locale={locale}
            locationData={data.locationData}
            metaDescription={data.metaDescription}
            metaTitle={data.metaTitle}
            modeData={data.modeData}
            noIndex={noIndex}
            originPlaceId={data.originPlaceId}
            path={path}
            searchParams={searchParams}
            showTrips={data.showTrips}
            trips={trips}
            widgetTitle={data.widgetTitle}
        />
    );
}

MultiModalCityToCityContainer.propTypes = {
    data: PropTypes.shape({
        arrivePlace: PropTypes.string,
        carousel: PropTypes.shape({
            slides: PropTypes.arrayOf(
                PropTypes.shape({
                    href: PropTypes.string,
                    imageCaption: PropTypes.string,
                    imageUrl: PropTypes.string,
                    title: PropTypes.string,
                })
            ),
            title: PropTypes.string,
        }),
        carriers: PropTypes.arrayOf(
            PropTypes.shape({
                carrierCode: PropTypes.string,
                name: PropTypes.string,
                pageSlug: PropTypes.string,
            }),
        ),
        departPlace: PropTypes.string,
        destinationPlaceId: PropTypes.string,
        headerImageUrl: PropTypes.string,
        id: PropTypes.string,
        locationData: PropTypes.shape({
            arrival: PropTypes.shape({
                formattedAddress: PropTypes.string,
                placeId: PropTypes.string,
            }),
            departure: PropTypes.shape({
                formattedAddress: PropTypes.string,
                placeId: PropTypes.string,
            }),
        }),
        metaDescription: PropTypes.string,
        metaTitle: PropTypes.string,
        modeData: PropTypes.shape({
            avg_bus_duration: PropTypes.number,
            avg_bus_price_usd: PropTypes.number,
            avg_daily_bus_trips: PropTypes.number,
            avg_daily_train_trips: PropTypes.number,
            avg_train_duration: PropTypes.number,
            avg_train_price_usd: PropTypes.number,
            cheapest: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
            fastest: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
            fastest_pre_departure: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
        }),
        originPlaceId: PropTypes.string,
        showTrips: PropTypes.bool,
        widgetTitle: PropTypes.string,
    }),
    fetchAllTrips: PropTypes.func,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }).isRequired,
    isFetchingAllTrips: PropTypes.bool,
    locale: PropTypes.string,
    noIndex: PropTypes.bool,
    path: PropTypes.string,
    psearchParams: PropTypes.object,
    searchParams: PropTypes.object,
    staticContext: PropTypes.shape({
        cache: PropTypes.shape({
            secondsFromNow: PropTypes.number,
        }),
        status: PropTypes.number,
        url: PropTypes.string,
    }),
    trips: PropTypes.arrayOf(PropTypes.shape({
        carrierCode: PropTypes.string,
        duration: PropTypes.number,
        pricing: PropTypes.array,
        tripId: PropTypes.string,
        tripModes: PropTypes.arrayOf(PropTypes.string),
    })),
};

MultiModalCityToCityContainer.serverFetchData = async (opts) => Widget.serverFetchData(opts);

const mapDispatchToProps = {
    fetchAllTrips: fetchAllTripsAction.fetch,
};

function mapStateToProps(state, ownProps) {
    const testTravelOptions = splitTestSelectors.selectTestVariant(state, 'bookableTravelOptions');
    const travelOptionsData = testTravelOptions === 'bookable_trips';

    const {
        url: path,
        params,
    } = ownProps.match;
    const {
        fromRegion,
        fromCity,
        toRegion,
        toCity,
    } = params;

    const url = `/${fromRegion}/${fromCity}/${toRegion}/${toCity}/`;
    const noIndex = !INDEXABLE_URLS.includes(url);
    const data = travelOptionsData
        ? testModeInfo[url]
        : modeInfo[url];

    const searchParams = {
        arrivePlace: encodeURIComponent(data?.arrivePlace),
        arrivePlaceId: data?.destinationPlaceId,
        departDate: i18nMoment().add(1, 'days').format(),
        departPlace: encodeURIComponent(data?.departPlace),
        departPlaceId: data?.originPlaceId,
        destinationPlaceId: data?.destinationPlaceId,
        originPlaceId: data?.originPlaceId,
    };

    const departDate = i18nMoment().format('YYYY-MM-DD');
    const psearchParams = data?.locationData
        ? getPSearchParams(data.locationData, departDate)
        : {};
    let isFetchingAllTrips = fetchAllTripsAction.selectWorking(state, psearchParams);
    const hasFetchedAllTrips = fetchAllTripsAction.selectSuccess(state, psearchParams)
        || fetchAllTripsAction.selectError(state, psearchParams);
    isFetchingAllTrips = isFetchingAllTrips || !hasFetchedAllTrips;

    return {
        data,
        isFetchingAllTrips,
        locale: selectLocale(state),
        noIndex,
        path,
        psearchParams,
        searchParams,
        trips: data?.showTrips
            ? c2cSelectors.selectBookableTrips(state, psearchParams)
            : undefined,
    };
}

export {
    MultiModalCityToCityContainer,
    mapStateToProps,
};
const LocalizedMultiModalCityToCity = localize(MultiModalCityToCityContainer, ['i18nMoment'], 'baseApp');
export default connect(mapStateToProps, mapDispatchToProps)(LocalizedMultiModalCityToCity);
